import React, { useCallback, useState } from "react";
import styled from "styled-components";
import { Check, ExternalLink, Info } from "react-feather";
import { useConnectorOAuthPopup } from "../../../../hooks/useConnectorOAuthPopup";
import { controlSize } from "../../../../design-system/tokens/controlSizes";
import { spacing } from "../../../../design-system/tokens/spacing";
import { typographyMixin } from "../../../../design-system/tokens/typography";
import { rawColors } from "../../../../design-system/tokens/colors";
import { borderRadius } from "../../../../design-system/tokens/borders";
import { PasswordVisibilityIcon } from "../../../atoms";
import { FormField, type FormFieldProps } from "../../../molecules/FormField";
import { FileUploadField } from "../../../molecules/FileUploadField";
import { TextAreaField } from "../../../molecules/TextAreaField";
import { OAuthButton } from "../../../molecules/OAuthButton";
import type {
  ConnectorFieldConfig,
  ConnectorFieldType,
  StepRenderProps,
} from "../ConnectorWizardModal.types";

/** Map config field types to the input subset FormField understands. */
const toInputType = (
  type: ConnectorFieldType,
): NonNullable<FormFieldProps["type"]> =>
  type === "select" || type === "textarea" || type === "file" ? "text" : type;

/** The ONLY field shown for an OAuth connector's credentials step. Everything
 *  else the schema generates (client_id/secret/refresh_token = app-owned +
 *  captured server-side; connection_mode/sandbox = defaulted) is intentionally
 *  hidden — the user just names the connection and clicks Connect. */
const OAUTH_VISIBLE_FIELD_KEYS = new Set(["connection_name"]);

/** Card padding + shadow taken straight from the Figma `Credentials` group:
 *  pt-[8px] px-[12px] pb-[12px], 16px gap between rows, 12px radius,
 *  subtle drop-shadow so the card lifts off the modal surface.
 *
 *  `min-height: 460px` matches the same constant used by Test/Select/Preview/
 *  Finish steps — pins a stable baseline so the modal doesn't visibly
 *  shrink/grow when you navigate between Credentials and the next step.
 *  Forms with more fields naturally exceed the baseline; shorter forms
 *  (e.g. 3-step BI configs with fewer credentials) just fill the floor. */
const Card = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[4]};
  width: 100%;
  min-width: 0;
  min-height:460px ;
  box-sizing: border-box;
  padding: ${spacing[2]} ${spacing[3]} ${spacing[3]};
  background-color: ${rawColors.structural.white};
  border: 1px solid ${rawColors.stroke[200]};
  border-radius: ${borderRadius.xl};
  box-shadow: 1px 1px 3px rgba(89, 98, 112, 0.1);
`;

const CardTitle = styled.h3`
  margin: 0;
  ${typographyMixin("body", 2)}
  color: ${rawColors.text.highEmphasis};
`;

/** Grid column count is config-driven via `step.columns`. The default of 2
 *  matches every existing connector; bump it to 3+ for denser forms. */
const FieldGrid = styled.div<{ $columns: number }>`
  display: grid;
  grid-template-columns: repeat(${({ $columns }) => $columns}, minmax(0, 1fr));
  gap: ${spacing[3]};
  width: 100%;
`;

/** Cell takes a final resolved span — the parent computes it (incl. the
 *  auto-stretch for an orphan last field) so this stays a dumb primitive. */
const FieldCell = styled.div<{ $span: number }>`
  grid-column: span ${({ $span }) => $span};
  display: flex;
  min-width: 0;
`;

const ApiErrorBanner = styled.p`
  margin: 0;
  ${typographyMixin("caption", 4)}
  color: ${rawColors.accent.red500};
`;

/** Full-width primary CTA for the OAuth "Connect with <provider>" action.
 *  Turns green once connected to signal the token was captured. */
const ConnectButton = styled.button<{ $connected?: boolean }>`
  all: unset;
  box-sizing: border-box;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${spacing[2]};
  width: 100%;
  padding: ${spacing[3]};
  border-radius: ${borderRadius.lg};
  background-color: ${({ $connected }) =>
    $connected ? rawColors.accent.green500 : rawColors.primary[600]};
  color: ${rawColors.text.white};
  ${typographyMixin("body", 3)}

  &:hover {
    background-color: ${({ $connected }) =>
      $connected ? rawColors.accent.green500 : rawColors.primary[700]};
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  &:focus-visible {
    outline: 2px solid ${rawColors.primary[500]};
    outline-offset: 2px;
  }
  svg {
    width: 18px;
    height: 18px;
  }
`;

const OAuthWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[3]};
  width: 100%;
  flex: 1 1 auto;
`;

/** Pinned to the bottom of the card (margin-top:auto) so the Connect button
 *  + hint sit under the fields with the empty space above them. */
const OAuthButtonGroup = styled.div`
  margin-top: auto;
  display: flex;
  flex-direction: column;
  gap: ${spacing[2]};
  width: 100%;
`;

const OAuthHint = styled.p`
  margin: 0;
  text-align: center;
  ${typographyMixin("caption", 4)}
  color: ${rawColors.text.mediumEmphasis};
`;

/** "Continue with <provider> | or | paste-a-link" row — used only when a
 *  step sets `oauthPairedFieldKey` (currently Google Sheets). Two equal
 *  columns with a centered "or" divider between them; the right column
 *  renders the paired field via the shared `renderField`, so it keeps the
 *  same label/validation/error styling as every other field. */
const PairedRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${spacing[3]};
  width: 100%;
`;

const PairedCell = styled.div`
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: ${spacing[2]};
`;

/** Sits beside the control row, not the label row above it — offset down by
 *  one caption line + the label-to-control gap, then vertically centered
 *  against the (fixed-height) control itself. */
const OrDivider = styled.span`
  flex: 0 0 auto;
  align-self: flex-start;
  margin-top: calc(1.2em + ${spacing[2]});
  height: ${controlSize.configForm.controlHeight};
  display: flex;
  align-items: center;
  ${typographyMixin("caption", 4)}
  color: ${rawColors.text.lowEmphasis};
`;

const ConnectedRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[2]};
  height: ${controlSize.configForm.controlHeight};
  padding: 0 ${spacing[3]};
  border-radius: ${borderRadius.lg};
  border: 1px solid ${rawColors.accent.green500};
  color: ${rawColors.accent.green500};
  ${typographyMixin("body", 3)}

  svg {
    width: 18px;
    height: 18px;
    flex: 0 0 auto;
  }
`;

const NoteRow = styled.p`
  margin: 0;
  display: flex;
  align-items: center;
  gap: ${spacing[2]};
  ${typographyMixin("caption", 4)}
  color: ${rawColors.text.mediumEmphasis};

  svg {
    width: 16px;
    height: 16px;
    flex: 0 0 auto;
  }
`;

const ToggleVisibilityButton = styled.button`
  all: unset;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${spacing[1]};
  border-radius: ${borderRadius.md};

  &:focus-visible {
    outline: 2px solid ${rawColors.primary[500]};
    outline-offset: 1px;
  }
`;

const SegmentedFieldWrap = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: ${spacing[2]};
`;

const SegmentedLabel = styled.label`
  display: inline-flex;
  align-items: center;
  gap: ${spacing[1]};
  margin: 0;
  ${typographyMixin("caption", 3)}
  color: ${rawColors.text.mediumEmphasis};
`;

const RequiredMark = styled.span`
  color: ${rawColors.accent.red500};
`;

const SegmentedShell = styled.div`
  display: flex;
  align-items: stretch;
  gap: ${spacing[1]};
  width: 100%;
  height: ${controlSize.configForm.controlHeight};
  min-height: ${controlSize.configForm.controlHeight};
  padding: ${spacing[1]};
  box-sizing: border-box;
  border: 1px solid ${rawColors.stroke[200]};
  border-radius: ${borderRadius.lg};
  background-color: ${rawColors.structural.white};
`;

const SegmentedButton = styled.button<{ $active: boolean; $disabled: boolean }>`
  all: unset;
  box-sizing: border-box;
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: ${({ $disabled }) => ($disabled ? "not-allowed" : "pointer")};
  text-align: center;
  padding: 0 ${spacing[3]};
  border-radius: ${borderRadius.md};
  ${typographyMixin("body", 3)}
  color: ${({ $active }) =>
    $active ? rawColors.text.highEmphasis : rawColors.text.mediumEmphasis};
  background-color: ${({ $active }) =>
    $active ? rawColors.primary[100] : rawColors.structural.white};
  opacity: ${({ $disabled }) => ($disabled ? 0.55 : 1)};

  &:focus-visible {
    outline: 2px solid ${rawColors.primary[500]};
    outline-offset: 1px;
  }
`;

const SegmentedHelper = styled.p<{ $error: boolean }>`
  margin: 0;
  display: flex;
  align-items: center;
  gap: ${spacing[2]};
  ${typographyMixin("caption", 4)}
  color: ${({ $error }) =>
    $error ? rawColors.accent.red500 : rawColors.text.mediumEmphasis};

  svg {
    width: 16px;
    height: 16px;
    flex: 0 0 auto;
  }
`;

/**
 * CredentialsStep — renders the configured field list in a 2-col grid.
 * Stateless w.r.t. field values: every value/error read flows in via
 * {@link StepRenderProps}, every mutation flows back through `setValue`. The
 * credential schema lives in `connectorWizardConfig.ts` so adding a connector
 * requires no UI change.
 *
 * When `step.oauthProvider` is set (Salesforce), the step instead leads with a
 * "Connect with <provider>" button that redirects to the provider's consent
 * screen — the backend callback mints the refresh_token server-side, so no
 * token is entered here. The raw credential fields remain reachable under an
 * "advanced" disclosure as a manual fallback.
 */
export const CredentialsStep: React.FC<StepRenderProps> = ({
  step,
  wizardConfig,
  catalogKey,
  values,
  errors,
  showErrors,
  apiError,
  setValue,
  setApiError,
  setCanAdvance,
}) => {
  const [passwordVisibleByKey, setPasswordVisibleByKey] = useState<
    Record<string, boolean>
  >({});
  const [fileErrorByKey, setFileErrorByKey] = useState<Record<string, string>>(
    {},
  );
  const [selectedFileByKey, setSelectedFileByKey] = useState<
    Record<string, string>
  >({});

  // OAuth success stays IN the wizard: record the connection the popup created
  // and enable the footer "Test connection" CTA. The user then clicks Test →
  // Airbyte (see handlePrimary's OAuth short-circuit + TestStep's OAuth branch).
  const { authorizing, startOAuth } = useConnectorOAuthPopup({
    onConnected: (connectionId) => {
      setValue("connectionId", connectionId);
      setValue("__sf_oauth", true);
      // The paired URL field (google_sheets' spreadsheet_url) is
      // deliberately NOT cleared here: if the user reached OAuth by pasting
      // a URL (Case 2), that value must survive and reach the backend as
      // config_json.spreadsheet_url so fetch_metadata scopes to just that
      // file instead of listing the whole Drive. Case 1 (button clicked
      // with an empty field) just leaves it empty, same as before.
      setCanAdvance(true);
    },
    onError: (message) => setApiError(message),
  });

  const isOAuthConnected =
    values["__sf_oauth"] === true &&
    typeof values["connectionId"] === "string" &&
    (values["connectionId"] as string).trim().length > 0;

  const togglePasswordVisible = useCallback((fieldKey: string) => {
    setPasswordVisibleByKey((prev) => ({
      ...prev,
      [fieldKey]: !prev[fieldKey],
    }));
  }, []);

  const fields: ConnectorFieldConfig[] = step.fields ?? [];

  const visibleFields = fields.filter(
    (field) => !field.visibleWhen || field.visibleWhen(values),
  );

  const columns = step.columns && step.columns > 0 ? step.columns : 2;

  /**
   * Resolved grid span for a single field: an explicit `colSpan` wins, then a
   * `width: "full"` fills the row, otherwise one cell. No orphan auto-stretch —
   * a lone last field keeps its natural half-width.
   */
  const spanFor = (field: ConnectorFieldConfig): number => {
    if (field.colSpan && field.colSpan > 0) return field.colSpan;
    if (field.width === "full") return columns;
    return 1;
  };

  const handleOAuthConnect = useCallback(() => {
    const rawName = values["connection_name"];
    const connectionName =
      typeof rawName === "string" && rawName.trim()
        ? rawName.trim()
        : wizardConfig.displayName;
    const isSandbox =
      values["is_sandbox"] === true || values["is_sandbox"] === "true";
    // Google Sheets: whatever the user typed into the paired URL field (if
    // any) must ride along on the authorize request — the backend's
    // build_config_json reads it from the OAuth state's `extra` and persists
    // it onto the connection, since the OAuth callback (not this form) is
    // what actually creates the connection row. Without this, a connection
    // made via the auto-triggered "paste URL -> OAuth" path would come back
    // with no spreadsheet_url and fail Test with "missing
    // config_json.spreadsheet_url". Undefined/blank for every other
    // connector and for Case 1 (button click, empty URL) — Salesforce has no
    // oauthPairedFieldKey so this is always undefined for it.
    const pairedFieldKey = step.oauthPairedFieldKey;
    const rawPairedValue = pairedFieldKey ? values[pairedFieldKey] : undefined;
    const spreadsheetUrl =
      typeof rawPairedValue === "string" && rawPairedValue.trim()
        ? rawPairedValue.trim()
        : undefined;
    const rawConnectionMode = values["connection_mode"];
    const connectionMode =
      typeof rawConnectionMode === "string" && rawConnectionMode.trim()
        ? rawConnectionMode.trim()
        : undefined;
    setApiError(null);
    // Opens the consent popup synchronously (inside this click, to dodge popup
    // blockers). The result returns via the hook's callbacks: onConnected →
    // navigate to the new connection, onError → show a message.
    void startOAuth({
      catalogKey,
      connectionName,
      isSandbox,
      spreadsheetUrl,
      connectionMode,
    });
  }, [values, catalogKey, wizardConfig.displayName, setApiError, startOAuth, step.oauthPairedFieldKey]);

  const renderField = (
    field: ConnectorFieldConfig,
    overrides?: {
      required?: boolean;
      disabled?: boolean;
      helperText?: string;
      onBlur?: () => void;
    },
  ) => {
    const rawValue = values[field.key];
    const stringValue = typeof rawValue === "string" ? rawValue : "";
    const dynamicHelperText = field.helperTextByValue?.[stringValue];
    const resolvedHelperText =
      overrides?.helperText ?? dynamicHelperText ?? field.helperText;
    const resolvedRequired = overrides?.required ?? field.required;
    const isFieldDisabled = Boolean(overrides?.disabled);
    const errorMessage = showErrors ? errors[field.key] : undefined;
    const fileError = fileErrorByKey[field.key];
    const resolvedError = fileError ?? errorMessage;
    const isPasswordField = field.type === "password";
    const isSegmentedSelect =
      field.type === "select" &&
      Array.isArray(field.options) &&
      field.options.length > 0;
    const passwordShown = Boolean(passwordVisibleByKey[field.key]);
    const inputType =
      isPasswordField && passwordShown ? "text" : toInputType(field.type);

    return (
      <FieldCell key={field.key} $span={spanFor(field)}>
        {isSegmentedSelect ? (
          <SegmentedFieldWrap>
            <SegmentedLabel>
              {field.label}
              {field.required ? (
                <RequiredMark aria-hidden="true">*</RequiredMark>
              ) : null}
            </SegmentedLabel>
            <SegmentedShell role="group" aria-label={field.label}>
              {field.options?.map((option) => {
                const active = stringValue === option.value;
                return (
                  <SegmentedButton
                    key={option.value}
                    type="button"
                    $active={active}
                    $disabled={Boolean(option.disabled)}
                    aria-pressed={active}
                    disabled={Boolean(option.disabled)}
                    onClick={() => setValue(field.key, option.value)}
                  >
                    {option.label}
                  </SegmentedButton>
                );
              })}
            </SegmentedShell>
            {resolvedError || resolvedHelperText ? (
              <SegmentedHelper $error={Boolean(resolvedError)}>
                {!resolvedError ? <Info aria-hidden="true" /> : null}
                <span>{resolvedError || resolvedHelperText}</span>
              </SegmentedHelper>
            ) : null}
          </SegmentedFieldWrap>
        ) : field.type === "textarea" ? (
          <TextAreaField
            label={field.label}
            required={field.required}
            placeholder={field.placeholder}
            helperText={resolvedHelperText}
            errorMessage={resolvedError}
            autoComplete={field.autoComplete}
            value={stringValue}
            rows={field.rows ?? 6}
            onChange={(e) => setValue(field.key, e.target.value)}
          />
        ) : field.type === "file" ? (
          <FileUploadField
            label={field.label}
            required={field.required}
            accept={field.fileAccept?.join(",")}
            selectedFileName={selectedFileByKey[field.key]}
            helperText={resolvedHelperText}
            errorMessage={resolvedError}
            onFileSelected={(file) => {
              const maxBytes = field.fileMaxBytes;
              if (typeof maxBytes === "number" && file.size > maxBytes) {
                setFileErrorByKey((prev) => ({
                  ...prev,
                  [field.key]: `File is too large. Max size is ${Math.floor(
                    maxBytes / 1024,
                  )} KB.`,
                }));
                return;
              }
              void file.text().then((content) => {
                setValue(field.key, content);
                setSelectedFileByKey((prev) => ({
                  ...prev,
                  [field.key]: file.name,
                }));
                setFileErrorByKey((prev) => {
                  if (!prev[field.key]) return prev;
                  const { [field.key]: _, ...rest } = prev;
                  return rest;
                });
              });
            }}
          />
        ) : (
          <FormField
            label={field.label}
            required={resolvedRequired}
            disabled={isFieldDisabled}
            type={inputType}
            placeholder={field.placeholder}
            helperText={resolvedHelperText}
            errorMessage={resolvedError}
            autoComplete={field.autoComplete}
            value={stringValue}
            onChange={(e) => setValue(field.key, e.target.value)}
            onBlur={overrides?.onBlur}
            endAdornment={
              isPasswordField ? (
                <ToggleVisibilityButton
                  type="button"
                  onClick={() => togglePasswordVisible(field.key)}
                  aria-label={
                    passwordShown ? "Hide password" : "Show password"
                  }
                >
                  <PasswordVisibilityIcon visible={passwordShown} />
                </ToggleVisibilityButton>
              ) : undefined
            }
          />
        )}
      </FieldCell>
    );
  };

  const isOAuthStep = Boolean(step.oauthProvider);
  // OAuth step shows the connection-name field (always) + whatever extra
  // keys this connector opts into via `oauthVisibleFieldKeys` (e.g. Google
  // Sheets also surfaces connection_mode + its sheet-URL field) + the
  // Connect button. Default (no opt-in) keeps Salesforce's existing
  // connection_name-only behavior unchanged.
  const oauthVisibleKeys = step.oauthVisibleFieldKeys?.length
    ? new Set([...OAUTH_VISIBLE_FIELD_KEYS, ...step.oauthVisibleFieldKeys])
    : OAUTH_VISIBLE_FIELD_KEYS;
  const oauthVisibleFields = visibleFields.filter((f) =>
    oauthVisibleKeys.has(f.key),
  );
  // Google Sheets pairs the OAuth button with a field ("Link to Google
  // sheet") in one row instead of the stacked fields-then-button layout
  // below. Salesforce has no `oauthPairedFieldKey`, so `pairedField` stays
  // undefined and it keeps its existing stacked ConnectButton UI untouched.
  //
  // Looked up from the unfiltered `fields` (not `visibleFields`) — the
  // paired row always renders regardless of any `visibleWhen` on the field
  // itself, so it never collapses to the stacked ConnectButton layout.
  const pairedField = step.oauthPairedFieldKey
    ? fields.find((f) => f.key === step.oauthPairedFieldKey)
    : undefined;
  // Case 2: pasting a URL, with or without clicking the button first, must
  // still acquire an OAuth token — same adapter/scope/popup as the button.
  // Triggered on blur (not on every keystroke) so a half-typed URL doesn't
  // repeatedly pop the consent window while the user is still typing.
  const pairedFieldValue =
    pairedField && typeof values[pairedField.key] === "string"
      ? (values[pairedField.key] as string)
      : "";
  const hasPairedFieldValue = pairedFieldValue.trim().length > 0;
  const handlePairedFieldBlur = useCallback(() => {
    if (!step.oauthPairedFieldKey) return;
    if (isOAuthConnected || authorizing) return;
    if (!hasPairedFieldValue) return;
    handleOAuthConnect();
  }, [
    step.oauthPairedFieldKey,
    isOAuthConnected,
    authorizing,
    hasPairedFieldValue,
    handleOAuthConnect,
  ]);

  return (
    <Card>
      {step.title ? <CardTitle>{step.title}</CardTitle> : null}
      {isOAuthStep ? (
        <OAuthWrap>
          {oauthVisibleFields.length > 0 ? (
            <FieldGrid $columns={columns}>
              {oauthVisibleFields.map(renderField)}
            </FieldGrid>
          ) : null}
          {pairedField ? (
            <PairedRow>
              <PairedCell>
                <SegmentedLabel>Google login</SegmentedLabel>
                {isOAuthConnected ? (
                  <ConnectedRow>
                    <Check aria-hidden="true" />
                    Connected
                  </ConnectedRow>
                ) : (
                  <OAuthButton
                    provider="google"
                    onClick={handleOAuthConnect}
                    disabled={authorizing}
                  />
                )}
              </PairedCell>
              <OrDivider>or</OrDivider>
              <PairedCell>
                {renderField(pairedField, {
                  // Never a hard-required field: leaving it empty and using
                  // the button (Case 1) is a fully valid path.
                  required: false,
                  helperText: isOAuthConnected
                    ? "Connected via Google."
                    : pairedField.helperText,
                  onBlur: handlePairedFieldBlur,
                })}
              </PairedCell>
            </PairedRow>
          ) : (
            <OAuthButtonGroup>
              <ConnectButton
                type="button"
                onClick={handleOAuthConnect}
                disabled={authorizing}
                $connected={isOAuthConnected}
              >
                {isOAuthConnected ? (
                  <Check aria-hidden="true" />
                ) : (
                  <ExternalLink aria-hidden="true" />
                )}
                {authorizing
                  ? "Waiting for sign-in…"
                  : isOAuthConnected
                    ? `Connected — reconnect ${wizardConfig.displayName}`
                    : `Connect with ${wizardConfig.displayName}`}
              </ConnectButton>
              <OAuthHint>
                {isOAuthConnected
                  ? "✓ Connected. Click “Test connection” below to continue."
                  : `You'll sign in to ${wizardConfig.displayName} in a popup — we never see your password.`}
              </OAuthHint>
            </OAuthButtonGroup>
          )}
        </OAuthWrap>
      ) : (
        <FieldGrid $columns={columns}>
          {visibleFields.map(renderField)}
        </FieldGrid>
      )}
      {step.note ? (
        <NoteRow>
          <Info aria-hidden="true" />
          <span>{step.note}</span>
        </NoteRow>
      ) : null}
      {apiError ? (
        <ApiErrorBanner role="alert">{apiError}</ApiErrorBanner>
      ) : null}
    </Card>
  );
};

export default CredentialsStep;
